import { useGetFormValidationError, useLocalization } from "@tm/localization"
import { TextField, Stack, Switch, Dialog, Typography, EditDialogHeaderButtons } from "@tm/components"
import { CompilationCreateRequest, CompilationUpdateRequest, useCreateCompilation, useUpdateCompilation } from "@tm/data/compilations"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { errorHandling } from "@tm/utils"
import { isWM } from "../../../../utils"
import { useFavoriteArticlesStore } from "../store"

type DialogCompilatoinToAdd = CompilationCreateRequest & { type: "add" }
type DialogCompilatoinToEdit = CompilationUpdateRequest & { type: "edit" }
export type DialogCompilation = DialogCompilatoinToAdd | DialogCompilatoinToEdit

type Props = {
    editFavoriteList: DialogCompilation
    onOpenCompilationDetails(id: string): void
}

export function EditDialog({ editFavoriteList, onOpenCompilationDetails }: Props) {
    const { translateText } = useLocalization()
    const getError = useGetFormValidationError()

    const { createCompilation, creatingCompilation } = useCreateCompilation(isWM())
    const { updateCompilation, updatingCompilation } = useUpdateCompilation(isWM())
    const { setCompilationToEdit: setEditFavoriteList } = useFavoriteArticlesStore()

    const form = useForm<DialogCompilation>({
        resolver: zodResolver(
            z.object({
                name: z.string().trim().min(1, getError("REQUIRED")).max(50),
                description: z.string().max(200).optional(),
                isPublic: z.boolean().optional(),
            })
        ),
        defaultValues: {
            ...editFavoriteList,
        },
    })

    const { handleSubmit, control } = form

    async function handleConfirmButtonClick(data: Record<string, unknown>) {
        if (editFavoriteList.type === "add") {
            const { type, ...rest } = editFavoriteList
            const result = await createCompilation({ ...rest, ...data })
            if (result) {
                onOpenCompilationDetails(result.compilationId)
            }
        } else {
            const { type, ...rest } = editFavoriteList
            await updateCompilation({ ...rest, ...data })
        }
        setEditFavoriteList(undefined)
    }

    function handleCloseButtonClick() {
        setEditFavoriteList(undefined)
    }

    return (
        <Dialog open position="middle" maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <Stack gap={2}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="h1">{translateText(editFavoriteList.type === "add" ? 13948 : 13925)}</Typography>
                            <EditDialogHeaderButtons
                                isSaving={creatingCompilation || updatingCompilation}
                                onCloseButtonClick={handleCloseButtonClick}
                            />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                            <Stack direction="row" gap={2} alignItems="center" flex="auto">
                                <Controller
                                    control={control}
                                    name="name"
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            sx={{ minWidth: "360px" }}
                                            label={translateText(155)}
                                            inputCounter
                                            inputProps={{ maxLength: 50 }}
                                            required
                                            size="extralarge"
                                            {...field}
                                            {...errorHandling(fieldState)}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            sx={{ width: "510px" }}
                                            label={translateText(617)}
                                            size="extralarge"
                                            inputCounter
                                            inputProps={{ maxLength: 200 }}
                                            {...field}
                                            {...errorHandling(fieldState)}
                                        />
                                    )}
                                />
                                {isWM() && (
                                    <Controller
                                        control={control}
                                        name="isPublic"
                                        render={({ field, fieldState }) => (
                                            <Switch
                                                labelPlacement="end"
                                                label={translateText(1254)}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                checked={field.value}
                                                {...errorHandling(fieldState)}
                                            />
                                        )}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </form>
            </FormProvider>
        </Dialog>
    )
}
